import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTheme } from "@/components/theme-provider";
import { Skeleton } from "@/components/ui/skeleton";
import { getAbsoluteUrl } from "@/lib/url-utils";

// Fallback logo in case settings fail to load
import fallbackLogo from "../assets/yapzap-logo.png";

// Cache for logos to prevent flashing during page loads
const logoCache = {
  desktopLight: null as string | null,
  desktopDark: null as string | null,
  mobileLight: null as string | null,
  mobileDark: null as string | null
};

interface SiteLogoProps {
  /** Force dark mode logo regardless of theme setting (for pages with dark backgrounds) */
  forceDarkMode?: boolean;
}

export const SiteLogo = ({ forceDarkMode = false }: SiteLogoProps) => {
  const { getAppropriateLogoUrl, getLogoHeight, isLoading, settings } = useSiteSettings();
  const { theme } = useTheme();
  // Use dark mode if forced or if theme is dark
  const effectiveTheme = forceDarkMode ? 'dark' : theme;
  const isDarkMode = effectiveTheme === 'dark';
  const [isMobile, setIsMobile] = useState(false);
  // Use a stable reference that doesn't change during component unmount/remount
  const [imgSrc, setImgSrc] = useState<string | null>(fallbackLogo);
  // Fixed default height values based on current settings
  const [currentHeight, setCurrentHeight] = useState<number>(isMobile ? 40 : 80);

  // Add window resize listener to detect mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Set initial value
    checkMobile();

    // Add event listener
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Get the appropriate logo URL based on device and theme
  // When forceDarkMode is true, we bypass the hook's theme detection and use dark logos directly
  // We need to compute the logo URL considering forceDarkMode even when settings are still loading
  let logoUrl: string | null = null;
  let logoHeight: number | null = null;

  if (forceDarkMode) {
    // When forcing dark mode, use dark logos directly from settings
    if (settings) {
      logoUrl = isMobile ? settings.logoMobileDark : settings.logoDesktopDark;
      logoHeight = isMobile ? settings.logoHeightMobileDark : settings.logoHeightDesktopDark;
      console.log('[SiteLogo] forceDarkMode=true, using dark logo:', logoUrl);
    } else {
      console.log('[SiteLogo] forceDarkMode=true but settings not loaded yet');
    }
    // If settings haven't loaded yet, logoUrl stays null and we'll use fallback
  } else {
    // Use the hook's theme-aware URL selection
    logoUrl = getAppropriateLogoUrl(isMobile);
    logoHeight = getLogoHeight(isMobile);
    console.log('[SiteLogo] forceDarkMode=false, using theme-aware logo:', logoUrl);
  }
  
  // Cache logo URLs globally to prevent flashing during navigation
  useEffect(() => {
    if (logoUrl) {
      if (isMobile) {
        if (isDarkMode) {
          logoCache.mobileDark = logoUrl;
        } else {
          logoCache.mobileLight = logoUrl;
        }
      } else {
        if (isDarkMode) {
          logoCache.desktopDark = logoUrl;
        } else {
          logoCache.desktopLight = logoUrl;
        }
      }
    }
  }, [logoUrl, isMobile, isDarkMode]);
  
  // Smoothly update height after we get the official value
  useEffect(() => {
    if (logoHeight && logoHeight !== currentHeight) {
      // Limit the height to reasonable values
      const maxAllowedHeight = isMobile ? 40 : 80;
      const constrainedHeight = Math.min(logoHeight, maxAllowedHeight);
      setCurrentHeight(constrainedHeight);

      // Log the updated height value for debugging
      const deviceType = isMobile ? 'mobile' : 'desktop';
      const themeType = isDarkMode ? 'dark' : 'light';
      console.log(`Logo height for ${deviceType} (${themeType} mode): ${constrainedHeight}px (requested: ${logoHeight}px)`);
    }
  }, [logoHeight, currentHeight, isMobile, isDarkMode]);
  
  // Set the image source when logoUrl changes or on initial load
  // Use cached value if available during initial load to prevent flashing
  useEffect(() => {
    // Determine which cached logo to use as a fallback
    let cachedLogo = null;
    if (isMobile) {
      cachedLogo = isDarkMode ? logoCache.mobileDark : logoCache.mobileLight;
    } else {
      cachedLogo = isDarkMode ? logoCache.desktopDark : logoCache.desktopLight;
    }

    // Use logo URL if available, otherwise use cached version, then fallback
    // Convert to absolute URL for Capacitor
    if (logoUrl) {
      setImgSrc(getAbsoluteUrl(logoUrl) || logoUrl);
    } else if (cachedLogo) {
      setImgSrc(getAbsoluteUrl(cachedLogo) || cachedLogo);
    } else {
      setImgSrc(fallbackLogo);
    }
  }, [logoUrl, isMobile, isDarkMode]);
  
  const handleImageError = () => {
    if (imgSrc !== fallbackLogo) {
      console.log("Using fallback logo instead of:", imgSrc);
      setImgSrc(fallbackLogo);
    }
  };
  
  // Use more reasonable dimensions for navbar (h-24 = 96px on desktop, h-16 = 64px on mobile)
  const containerHeight = isMobile ? 40 : 80; // Fits within 96px navbar with padding on desktop
  const maxWidth = isMobile ? 120 : 240; // Increased proportionally
  
  return (
    <Link href="/">
      <div className="flex items-center cursor-pointer" style={{ 
        height: `${containerHeight}px`, 
        maxWidth: `${maxWidth}px`,
        position: 'relative'
      }}>
        {/* Actual logo */}
        {!isLoading && (
          <img 
            src={imgSrc || fallbackLogo} 
            alt="YapZap Logo" 
            style={{ 
              height: `${containerHeight}px`,
              maxHeight: `${containerHeight}px`,
              width: 'auto',
              maxWidth: `${maxWidth}px`,
              objectFit: 'contain',
            }}
            className="drop-shadow-[0_0_10px_rgba(93,63,211,0.5)] hover:drop-shadow-[0_0_15px_rgba(93,63,211,0.7)] transition-all duration-200 hover:scale-105"
            onError={handleImageError}
          />
        )}
        
        {/* Skeleton loader for initial loading */}
        {isLoading && (
          <Skeleton 
            style={{
              height: `${containerHeight}px`,
              width: `${maxWidth}px`,
            }}
          />
        )}
      </div>
    </Link>
  );
};

export default SiteLogo;